import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EXERCISES, ExerciseId, Question, chordWords, makeQuestion } from '../../data/training';
import { playlist } from '../../data/corpus';
import { CHORD_WORDS } from '../copy';
import { shortcutBlocked, usePlayer, usePrefs } from '../hooks';
import { PlayButton } from '../components/PlayButton';
import { SubTabs } from '../components/Shell';
import { requestPlay } from '../intent';

/** Play chords, or train the ear: the two ways to make sound yourself. */
export function PlayTabs({ current }: { current: 'walk' | 'train' }) {
  return (
    <SubTabs
      current={current}
      items={[
        { id: 'walk', href: '#walk', label: 'Play chords' },
        { id: 'train', href: '#train', label: 'Train your ear' },
      ]}
    />
  );
}

interface Tally {
  right: number;
  total: number;
  run: number;
  best: number;
}

const STORE = 'gravity-atlas:train:v1';
const empty = (): Tally => ({ right: 0, total: 0, run: 0, best: 0 });

function loadScores(): Record<string, Tally> {
  try {
    const raw = window.localStorage.getItem(STORE);
    return raw ? (JSON.parse(raw) as Record<string, Tally>) : {};
  } catch {
    return {};
  }
}

function saveScores(s: Record<string, Tally>): void {
  try {
    window.localStorage.setItem(STORE, JSON.stringify(s));
  } catch {
    /* storage unavailable: scores last for this visit */
  }
}

/** "Arrives home" → "arrives home"; the schemata keep their names (Prinner, Romanesca). */
const lowerFirst = (x: string) => (/^(Prinner|Romanesca|Fonte|Monte|Ponte|Quiescenza)\b/.test(x) ? x : x.charAt(0).toLowerCase() + x.slice(1));

export function Train() {
  const { prefs } = usePrefs();
  const { depth } = prefs;
  const { player, position, playing } = usePlayer();
  const [exercise, setExercise] = useState<ExerciseId>('endings');
  const [harder, setHarder] = useState(false);
  const [question, setQuestion] = useState<Question | null>(null);
  const [chosen, setChosen] = useState<string | null>(null);
  const [scores, setScores] = useState<Record<string, Tally>>(() => loadScores());
  const [segments, setSegments] = useState<Array<{ offset: number; length: number }>>([]);
  const panel = useRef<HTMLElement>(null);
  useEffect(() => player.setTempo(prefs.tempo), [player, prefs.tempo]);

  const words = useCallback((rn: string) => chordWords(rn, CHORD_WORDS), []);
  const play = useCallback(
    (q: Question) => {
      const p = playlist(q.clips, q.normalize, 1.2);
      setSegments(p.segments.map((s) => ({ offset: s.offset, length: s.length })));
      player.setTempoMap(p.tempoMap);
      player.setNotes(p.notes, p.total);
      void player.play(0);
    },
    [player],
  );

  const next = useCallback(
    (ex: ExerciseId = exercise, autoplay = true) => {
      const q = makeQuestion(ex, Math.random, { harder, depth, words });
      setQuestion(q);
      setChosen(null);
      if (autoplay) play(q);
    },
    [exercise, harder, depth, words, play],
  );

  const choose = useCallback(
    (id: string) => {
      if (!question || chosen) return;
      setChosen(id);
      const right = id === question.answer;
      setScores((all) => {
        const t = { ...(all[question.exercise] ?? empty()) };
        t.total += 1;
        if (right) {
          t.right += 1;
          t.run += 1;
          t.best = Math.max(t.best, t.run);
        } else t.run = 0;
        const out = { ...all, [question.exercise]: t };
        saveScores(out);
        return out;
      });
    },
    [question, chosen],
  );

  // Keys: 1–4 answer, R replays, N or Enter goes on.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName;
      if (shortcutBlocked(e) || !question) return;
      const n = Number(e.key);
      if (n >= 1 && n <= question.choices.length) choose(question.choices[n - 1].id);
      else if (e.key === 'r' || e.key === 'R') play(question);
      else if ((e.key === 'n' || e.key === 'N' || (e.key === 'Enter' && tag !== 'BUTTON' && tag !== 'A')) && chosen) next();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [question, chosen, choose, play, next]);

  const start = (ex: ExerciseId) => {
    setExercise(ex);
    next(ex, true);
    requestAnimationFrame(() => panel.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' }));
  };

  const tally = scores[exercise] ?? empty();
  const info = EXERCISES.find((x) => x.id === exercise)!;
  const clipNow = playing && segments.length > 1 ? segments.findIndex((s) => position >= s.offset - 1e-6 && position < s.offset + s.length + 1.2) : -1;
  const right = chosen !== null && question && chosen === question.answer;
  const answerChoice = question?.choices.find((c) => c.id === question.answer);
  const playLabel = question ? (question.clips.length > 1 ? 'Play both, one after the other' : 'Play the excerpt') : 'Play';

  const summary = useMemo(
    () =>
      EXERCISES.map((x) => {
        const t = scores[x.id];
        return { ...x, line: t && t.total ? `${t.right} of ${t.total} right · best run ${t.best}` : 'Not tried yet' };
      }),
    [scores],
  );

  return (
    <div className="train">
      <header className="page-head">
        <span className="label">Play</span>
        <h1>Train your ear</h1>
        <p>Short listening questions made from real music in the atlas. There is no clock: listen as often as you like, then answer.</p>
      </header>
      <PlayTabs current="train" />

      <ul className="exercise-list" aria-label="Exercises">
        {summary.map((x) => (
          <li key={x.id}>
            <button className="exercise" aria-pressed={question !== null && exercise === x.id} onClick={() => start(x.id)}>
              <b>{x.title}</b>
              <span>{x.blurb}</span>
              <small>{x.line}</small>
            </button>
          </li>
        ))}
      </ul>

      <section className="panel quiz" ref={panel} aria-live="polite">
        {!question ? (
          <div className="quiz-empty">
            <p>Pick an exercise above to begin. Each question plays by itself; press play to hear it again.</p>
            <button className="btn primary" onClick={() => start('endings')}>
              Start with “How does it end?”
            </button>
          </div>
        ) : (
          <>
            <div className="panel-head">
              <span className="label">{info.title}</span>
              <span className="muted quiz-score">
                {tally.right} of {tally.total} right{tally.run > 1 ? ` · ${tally.run} in a row` : ''}
              </span>
            </div>
            <h2 className="quiz-prompt">{question.prompt}</h2>
            <div className="quiz-play">
              <PlayButton playing={playing} label={playLabel} onPause={() => player.pause()} onPlay={() => play(question)} />
              {question.clips.length > 1 && (
                <span className="quiz-clips" aria-hidden="true">
                  <span data-on={clipNow === 0}>first</span>
                  <span data-on={clipNow === 1}>second</span>
                </span>
              )}
              {exercise === 'endings' && (
                <label className="field">
                  <input id="harder" type="checkbox" checked={harder} onChange={(e) => setHarder(e.target.checked)} /> Harder: add “arrives, but softly”
                </label>
              )}
            </div>
            <div className="quiz-choices">
              {question.choices.map((c, i) => {
                const state = chosen === null ? undefined : c.id === question.answer ? 'right' : c.id === chosen ? 'wrong' : 'other';
                return (
                  <button key={c.id} className="quiz-choice" data-state={state} disabled={chosen !== null} onClick={() => choose(c.id)}>
                    <span className="quiz-key" aria-hidden="true">
                      {i + 1}
                    </span>
                    <span>
                      <b>{c.label}</b>
                      {depth >= 2 && c.name && <small>{c.name}</small>}
                    </span>
                  </button>
                );
              })}
            </div>
            {chosen !== null && (
              <div className="quiz-feedback" data-right={right}>
                <p className="quiz-verdict">{right ? 'Right.' : `Not quite: ${lowerFirst(answerChoice?.label ?? '')}.`}</p>
                <p>{question.explain}</p>
                {depth >= 2 && question.named && <p className="muted">{question.named}</p>}
                <div className="quiz-actions">
                  <button className="btn primary" onClick={() => next()}>
                    Next question
                  </button>
                  <button className="btn" onClick={() => play(question)}>
                    Hear it again
                  </button>
                  <a
                    href={question.link.href}
                    onClick={() => {
                      const [id, moment] = question.link.href.slice(1).split('.');
                      if (!['tune', 'path'].includes(id)) requestPlay(id, moment);
                    }}
                  >
                    {question.link.label} →
                  </a>
                </div>
              </div>
            )}
            <p className="muted quiz-keys">Keys: 1–{question.choices.length} to answer, R to hear it again, N for the next question.</p>
          </>
        )}
      </section>
    </div>
  );
}
